import { authInitialiser } from '../../utils/auth'
import auth0Client from '../../utils/auth/auth0Client'
import { logout } from '../../utils/api'
import LocalStorage from '../../utils/LocalStorage'
import { siteUrl } from '../../constants'
import { clientId } from '../../constants/auth'

const extractError = (error) => {
  const defaultErrorMessage = 'An unknown error occurred.'
  if (error) {
    return error.error_description ||
      error.description ||
      error.error ||
      error.code ||
      defaultErrorMessage
  }
  return null
}

export default {
  /**
   * Initialise the authentication on page load.
   *
   * @param {Function} commit
   * @param {Function} dispatch
   * @return {Promise}
   */
  async initialize({ commit, dispatch }) {
    let initialisationResolver

    commit('setLoading', true)
    commit('setInitialisationPromise', new Promise((resolve) => {
      initialisationResolver = resolve
    }))

    const { error, session, userError, user, userDataError, userData } = await authInitialiser.run()
    const authenticated = Boolean(session && session.accessToken)

    if (error && error.code !== 'login_required') {
      commit('setError', extractError(error))
    }

    commit('setAuthenticated', authenticated)

    if (authenticated) {
      if (userError || !user) {
        commit('setError', extractError(userError))
        commit('setAuthenticated', false)
      } else if (userDataError || !userData) {
        dispatch('notifications/flash/create', {
          type: 'error',
          content: 'Error fetching user details',
        }, { root: true })
        throw userDataError
      } else {
        commit('setUser', { ...user, ...userData })
        commit('setError', undefined)
      }
    }

    if (/^#access_token=.+/.test(window.location.hash)) {
      window.location.hash = ''
    }

    initialisationResolver()

    commit('setLoading', false)
    commit('setInitialisationPromise', null)
  },

  async login({ commit }, { username, email, password, returnTo }) {
    commit('setError', '')

    const response = await new Promise((s) => auth0Client.login({
      username,
      email,
      password,
      realm: 'Username-Password-Authentication',
      responseType: 'token',
      redirectUri: returnTo || `${siteUrl}`,
    }, s))

    if (response) {
      commit('setError', extractError(response))
    }
  },

  async logout({ dispatch }, { returnTo } = {}) {
    await logout().catch((e) => {
      dispatch('notifications/flash/create', {
        type: 'error',
        content: 'Failed to log out. Please try again',
      }, { root: true })

      throw e
    })

    LocalStorage.clear()

    auth0Client.logout({
      returnTo: returnTo || `${siteUrl}/logout-confirmation`,
      clientID: clientId,
    })
  },

  resetPassword({ state }, { email: _email } = {}) {
    const email = _email || state.user.email

    return new Promise((resolve, reject) => (
      auth0Client.changePassword({
        connection: 'Username-Password-Authentication',
        email,
      }, (error, response) => {
        if (error) {
          reject(error)
        } else {
          resolve(response)
        }
      })
    ))
  },

  /**
   * Verify that the current user is authenticated. If not, a message will be displayed and an exception thrown.
   *
   * @param {Object} state
   * @param {function} dispatch
   */
  verifyAuthentication({ state, dispatch }) {
    if (!state.isAuthenticated) {
      dispatch('notifications/flash/create', {
        type: 'error',
        content: 'You must be logged in to add items to your basket.',
      }, { root: true })

      throw new Error('Access denied')
    }

    return true
  },

  /**
   * Set a value as the error.
   *
   * @param {Function} commit
   * @param {String|null} error
   */
  setError({ commit }, { error }) {
    commit('setError', error)
  },
}
